import { useCallback, useContext } from 'react';
import { Platform, ToastAndroid } from 'react-native';
import { ExtrasContext } from '../Context/ExtrasContext';
import { useTheme } from '../theme';
import { useAppSelector } from '../redux/helper';
import { selectUnseenNotificationsCount } from '../redux/notificationsSlice';
import ChatwootService from '../services/ChatwootService';
import useChatwootSnapshot from './useChatwootSnapshot';
import { pickHeaderStatusSlot } from '../utils/headerStatusSlot';
import type { HeaderIconAction } from '../components/Reusables/headers/HeaderActions';

const ALERT_COLOR = '#FF9800';

/**
 * The phone header's single status icon (see utils/headerStatusSlot).
 * `fallback` is what the screen shows when nothing is pending; without one
 * it is the plain notifications bell.
 */
const useHeaderStatusAction = ({
  onOpenNotifications,
  fallback,
}: {
  onOpenNotifications: () => void;
  fallback?: HeaderIconAction;
}): HeaderIconAction => {
  const { theme } = useTheme();
  const { networkAvailableGlobal } = useContext(ExtrasContext);
  const chatwootSnapshot = useChatwootSnapshot();
  const unreadNotificationsCount = useAppSelector(
    selectUnseenNotificationsCount,
  );

  const handleOfflineTap = useCallback(() => {
    if (Platform.OS !== 'android') return;
    ToastAndroid.show(
      'You are currently offline. Please check your internet connection and try again.',
      ToastAndroid.SHORT,
    );
  }, []);

  const slot = pickHeaderStatusSlot({
    online: networkAvailableGlobal,
    chatUnread: chatwootSnapshot.unread.hasUnread,
    notificationsUnread: unreadNotificationsCount,
  });

  switch (slot) {
    case 'offline':
      return {
        type: 'icon',
        icon: 'wifi-off',
        onPress: handleOfflineTap,
        accessibilityLabel: 'No internet connection',
        iconColor: theme.colors.textSecondary,
      };
    case 'chat':
      return {
        type: 'icon',
        icon: 'chatbubbles-sharp',
        iconLibrary: 'ionicons',
        badgeCount: chatwootSnapshot.unread.unreadCount,
        showBadge: true,
        iconColor: ALERT_COLOR,
        onPress: () => void ChatwootService.openChat(),
        accessibilityLabel: 'Customer support',
      };
    case 'notifications':
      return {
        type: 'icon',
        icon: 'notifications',
        iconLibrary: 'ionicons',
        badgeCount: unreadNotificationsCount,
        showBadge: true,
        iconColor: ALERT_COLOR,
        onPress: onOpenNotifications,
        accessibilityLabel: 'Notifications',
      };
    default:
      return (
        fallback ?? {
          type: 'icon',
          icon: 'notifications-outline',
          iconLibrary: 'ionicons',
          onPress: onOpenNotifications,
          accessibilityLabel: 'Notifications',
        }
      );
  }
};

export default useHeaderStatusAction;
